import type { Editor } from '@open-pencil/core/editor'
import { randomHex } from '@open-pencil/scene-graph/random'
import type { VariableCollection } from '@open-pencil/scene-graph'
import type { Color } from '@open-pencil/scene-graph/primitives'

import { useOpenPencilBindingProvider } from '#react/controls/binding/open-pencil'
import type { BindingTarget } from '#react/controls/binding/types'

const FALLBACK_COLOR_VARIABLE_NAME = 'New color'

function colorCollection(editor: Editor): VariableCollection {
  const existing = editor
    .getCollections()
    .find((collection) =>
      collection.variableIds.some((variableId) => editor.getVariable(variableId)?.type === 'COLOR')
    )
  if (existing) return existing

  const collection: VariableCollection = {
    id: `col:${randomHex(8)}`,
    name: 'Colors',
    modes: [{ modeId: 'default', name: 'Mode 1' }],
    defaultModeId: 'default',
    variableIds: []
  }
  editor.addCollection(collection)
  return collection
}

export function createAndBindColorVariable(
  editor: Editor,
  target: BindingTarget,
  value: Color,
  name = FALLBACK_COLOR_VARIABLE_NAME
) {
  const collection = colorCollection(editor)
  const id = `var:${randomHex(8)}`
  editor.addVariable({
    id,
    name: name.trim() || FALLBACK_COLOR_VARIABLE_NAME,
    type: 'COLOR',
    collectionId: collection.id,
    valuesByMode: Object.fromEntries(
      collection.modes.map((mode) => [mode.modeId, structuredClone(value)])
    ),
    description: '',
    hiddenFromPublishing: false
  })
  editor.bindVariable(target.nodeId, target.path, id)
}

export function setColorVariableValue(editor: Editor, variableId: string, value: Color) {
  const variable = editor.getVariable(variableId)
  if (!variable) return
  const collection = editor.getCollection(variable.collectionId)
  if (!collection) return
  for (const mode of collection.modes)
    editor.updateVariableValue(variableId, mode.modeId, structuredClone(value))
}

export function useColorBindingProvider() {
  return useOpenPencilBindingProvider<Color>({
    type: 'COLOR',
    resolve: (editor, variableId) => editor.resolveColorVariable(variableId),
    create: createAndBindColorVariable,
    setValue: setColorVariableValue
  })
}
